import type { SharkLife } from './sharks';
import type { SiteId } from './data/sites';
import { REEF_SURVEYS } from './data/fish/reefSurveys';
import { MAUI_NUI_HARD_BOTTOM_KM2 } from './fish';
import { WHITETIP_YOUNG_SURVIVAL } from './data/sharks/whitetip';

/**
 * The whitetip reef shark, *Triaenodon obesus*, the manō lālākea (M219): a small shark of the reef's caves and ledges,
 * "At depths of about 25-130 ft near coral reefs; seen resting in caves, sometimes for extended periods" (DLNR's
 * Hawaiʻi Sharks page), hunting the reef's fish and octopus by night ("those identified were all reef fishes such as
 * scarids and acanthurids; four had eaten octopuses", of 56 stomachs, Randall 1977, *Pacific Science* 31: 143, p. 143,
 * its rights reserved, its facts cited). It keeps to its reef: of 124 tagged at Johnston Atoll, the 7 caught again were
 * 0.3 to 2.9 km from where they were tagged (Randall 1977, p. 143). Stepped as the tiger shark is (`stepTigerSharks`):
 * by year class since birth, its pups born in the pupping season, every shark dying at its age's rate, on its own stream.
 *
 * **Its life is the Great Barrier Reef's**: no Hawaiian whitetip has been aged. Hisano, Connolly and Robbins 2011
 * ("Population growth rates of reef sharks with and without fishing on the Great Barrier Reef", *PLoS ONE* 6: e25028,
 * "distributed under the terms of the Creative Commons Attribution License") fitted its growth, maturity and litters
 * from Robbins's 134 sharks (Text S1); Hawaiʻi's own, Whitney et al. 2012 (its publisher's login refused here) and Nadon
 * et al. 2012 (its hosts refused), were not read (open, asked of the user).
 *
 * **Its count is NOAA's divers'** (`REEF_SURVEYS.whitetip`, MEASURED, `scripts/reefSurveys.ts`): 0.0557 a hectare of
 * the hard bottom it surveys, 0 to 30 m, from the 2 whitetips counted within the cylinders of 520 site visits, 2013 to 2024 (14
 * seen at 10 visits, any count type), times Maui Nui's 269 km² of hard bottom to 30 m, the area NOAA's density is
 * reckoned over (`MAUI_NUI_HARD_BOTTOM_KM2`, as the manini's and the nenue's are; review M219's: not the reef's whole
 * floor, sand among it): 1,498 sharks of every size (DERIVED, `WHITETIP_COUNT`). Two sharks are a
 * thin count: a Poisson count of 2 has its mean between 0.24 and 7.2 at 95%, so 180 to 5,400 sharks (DERIVED). Divers
 * count it fairly: its "sedentary daytime behavior ... makes them highly susceptible to visual survey methods" (Dale et
 * al. 2011, *PLoS ONE*, CC BY), unlike the tiger shark.
 * "The whitetip reef shark is rare in the principal Hawaiian Islands (Kauai to Hawaii)" (Randall 1977, p. 147): open,
 * against the count.
 *
 * Its one fitted rate the young's survival (`WHITETIP_YOUNG_SURVIVAL`), set so the population replaces itself: no
 * trend is measured, and it is protected: "Unlawful to capture or entangle any shark or ray, whether alive or dead, or
 * kill any shark or ray" (DAR's rules booklet, 2025, p. 8).
 */
export const WHITETIP_SHARK: SharkLife = {
  id: 'whitetip',
  /**
   * Hisano et al. 2011, Table S1.1, the females' fit, chosen over the sexes combined "for T. obesus" by AIC (Text S1):
   * L∞ 208 cm, K 0.05 a year, t0 −9.8 years (95% intervals 167 to 236 cm, 0.03 to 0.09 and −12.2 to −6.9). Its length
   * at age 0 the fit's own, 208 (1 − e^(−0.05 · 9.8)), 80.6 cm (DERIVED; review M219's: Randall's newborn, 43.2 cm
   * precaudal, p. 143, about 59.2 cm total, p. 149, would lower every age's length off Hisano's curve), so the curve is
   * Hisano's: 122.6 cm at 8 years, the size at maturity's top, "females = 114–122 cm TL (8 yr)" (Mukai et al. 2022,
   * p. 24). The largest: "the
   * largest of 231 individuals measured by the author and associates was 168 cm total length" (Randall 1977, p. 143).
   */
  growthPerYear: 0.05,
  asymptoteCm: 208,
  birthCm: 208 * (1 - Math.exp(-0.05 * 9.8)),
  largestMeasuredCm: 168,
  /**
   * "The best estimate of age at 50% maturity from the regression was 8 yrs for T. obesus with a 95% bootstrap confidence
   * interval of 8-9 yrs" (Hisano et al. 2011, Text S1), the females'. The males' not given there: taken as the females'
   * (unused: the pups are the females').
   */
  femaleMatureYears: 8,
  maleMatureYears: 8,
  /**
   * "Mean litter size was 2.1 by the raw data with a 95% bootstrap confidence interval of 1.7-2.5", of 14 pregnant
   * females; "halved further for the biennial periodicity of breeding" (Hisano et al. 2011, Text S1): a litter every two
   * years, the model's assumption, not measured (Randall's 13 litters, "one to five", 2.77 on average, p. 160).
   */
  litterPups: [2.1, 2.1],
  yearsBetweenLitters: 2,
  /**
   * Hamel and Cope's estimator from growth, "M = 1.55k" (2022, Eq. 11), as the tiger shark's (`sharks.ts`): 1.55 × 0.05,
   * 0.0775 a year (DERIVED), within Hisano et al.'s own estimates, "ranging from 0.04–0.17 year⁻¹" (p. 5). Their
   * estimator from the oldest age, 5.40 / 19, 0.284, is outside them: the oldest aged of a fished sample, which Hisano et
   * al. hold "biased low as an estimate of longevity" (p. 4). From female maturity on; before it, the fitted young's.
   */
  adultDeathPerYear: 1.55 * 0.05,
  /** "a longevity of 25 years as the maximum age in our baseline simulations (this is the maximum reported age globally for these species)" (Hisano et al. 2011, p. 4): its sharks dead at the year's turn. */
  oldestYears: 25,
  youngSurvival: WHITETIP_YOUNG_SURVIVAL,
  /** "halved these estimates of average litter sizes to account for 1:1 sex ratio of gestating pups" (Hisano et al. 2011, Text S1); "The sex ratio does not deviate significantly from 1 : 1 except for whitetips caught in traps" (Randall 1977, p. 143). */
  femaleShare: 0.5,
  /**
   * Hawaiʻi's pups born "May through early June" (Mukai et al. 2022, NOAA PIFSC Data Report DR-22-019, p. 25, a US
   * government work, from Whitney 2012, unread): the first of May to the tenth of June, "early June" read as its first
   * ten days (JUDGED).
   */
  pupping: { from: [5, 1], to: [6, 10] },
  /**
   * Every size: NOAA's divers count every fish of a species in the cylinder, whatever its length, so the count holds the
   * pups too (the two they counted 95 and 110 cm, `REEF_SURVEYS.whitetip`).
   */
  countedFromCm: 0,
};

/** Maui Nui's hard bottom to 30 m, ha: the area NOAA's density is reckoned over (`MAUI_NUI_HARD_BOTTOM_KM2`, Nadon 2017's). */
export const WHITETIP_REEF_HA = MAUI_NUI_HARD_BOTTOM_KM2 * 100;

/** Maui Nui's whitetips of the counted sizes (above): the divers' density a hectare times Maui Nui's hard bottom. */
export const WHITETIP_COUNT = REEF_SURVEYS.whitetip.perHa * WHITETIP_REEF_HA;

/** Whether a site has the whitetips as read: Maui Nui, where NOAA's divers counted them. */
export const siteHasWhitetips = (siteId: SiteId): boolean => siteId === 'maui_nui';
